// FILE: WarehouseInventoryProduct.js
// CHANGELOG (Per Gideon docs + per-tab API loading):
// 1) Uses new APIs (Main, Physical Storage, History).
// 2) Each tab has its own loader + error.
// 3) Correctly reads API shape: response = { message, data: { product: {...} } }.
// 4) Prevents repeated calls when tab data is legitimately empty.
// 5) Restored wording/links to "Warehouse Inventory" (not warehouse_inventory).

import React, { useEffect, useMemo, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { Block, BlockHead, BlockTitle } from "../../../components/Component";
import PageHeading from "../components/page_heading/PageHeading";
import { useParams } from "react-router";
import { Spinner } from "reactstrap";
import CardTabs from "../components/cards/CardTabs";
import { employee_dashboard_actions_helper } from "../../../actions/actions_helper";
import { useDispatch, useSelector } from "react-redux";
import * as app_consts from "../../../constants/react_app_consts";
import { messageToast } from "../../../utils/Utils";
import ProfileDetailText from "../components/text/ProfileDetailText";
import ReadOnlyTable from "../components/table/ReadOnlyTable";
import WarehouseInventoryModel from "../components/modal/warehouse_inventory_modal/WarehouseInventoryModel";

// NEW APIs (from guides/postman)
import {
  RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_MAIN_DETAILS_API,
  RETRIEVE_WAREHOUSE_INVENTORY_PHYSICAL_STORAGE_PRODUCT_DETAILS_API,
  RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_HISTORY_DETAILS_API,
} from "../../../api/api_data";

// NEW Slice Actions
import {
  fail_retrieve_warehouse_inventory_product_main_details,
  loading_retrieve_warehouse_inventory_product_main_details,
  reset_retrieve_warehouse_inventory_product_main_details,
  success_retrieve_warehouse_inventory_product_main_details,
} from "../../../reducers/dashboard_reducers/warehouse/retrieve_warehouse_inventory_product_main_details_Slice";

import {
  fail_retrieve_warehouse_inventory_physical_storage_product_details,
  loading_retrieve_warehouse_inventory_physical_storage_product_details,
  reset_retrieve_warehouse_inventory_physical_storage_product_details,
  success_retrieve_warehouse_inventory_physical_storage_product_details,
} from "../../../reducers/dashboard_reducers/warehouse/retrieve_warehouse_inventory_physical_storage_product_details_Slice";

import {
  fail_retrieve_warehouse_inventory_product_history_details,
  loading_retrieve_warehouse_inventory_product_history_details,
  reset_retrieve_warehouse_inventory_product_history_details,
  success_retrieve_warehouse_inventory_product_history_details,
} from "../../../reducers/dashboard_reducers/warehouse/retrieve_warehouse_inventory_product_history_details_Slice";

// Messages
import {
  RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ACCESS_DENIED_MSG,
  RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ERR_MSG,
} from "../../../constants/success_error_messages_consts";

const warehouse_list_type = app_consts.WAREHOUSE_INVENTORY_BRANCHES_LIST;
const inventory_history_list_type = app_consts.INVENTORY_HISTORY_LIST;
const page_type = app_consts.PRODUCT_WAREHOUSE_INVENTORY_PAGE;

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const WarehouseInventoryProduct = () => {
  const { product_id } = useParams();
  const dispatch = useDispatch();

  const [activeCardTab, setActiveCardTab] = useState("Summary");

  // Summary tab state
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [summaryData, setSummaryData] = useState(null);

  // Physical tab state
  const [physicalLoading, setPhysicalLoading] = useState(false);
  const [physicalError, setPhysicalError] = useState("");
  const [warehouse_list_data, setWarehouseListData] = useState([]);

  // History tab state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [inventory_history_data, setInventoryHistoryData] = useState([]);

  // Fetch flags (prevents re-fetch on tab switch even if empty list)
  const [physicalFetched, setPhysicalFetched] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);

  // Other UI state
  const [product_name, setProductName] = useState("");
  const [isInventoryModuleOpen, setInventoryModalOpen] = useState(false);
  const [selectedAdjustID, setSelectedAdjustID] = useState("");

  // -------------------- API DISPATCHERS --------------------

  const fetchMainDetails = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_warehouse_inventory_product_main_details,
        success_retrieve_warehouse_inventory_product_main_details,
        fail_retrieve_warehouse_inventory_product_main_details,
        RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ERR_MSG,
        null,
        app_consts.GET,
        `${RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_MAIN_DETAILS_API}${product_id}`,
        null,
        RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ACCESS_DENIED_MSG
      )
    );
  };

  const fetchPhysicalStorageDetails = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_warehouse_inventory_physical_storage_product_details,
        success_retrieve_warehouse_inventory_physical_storage_product_details,
        fail_retrieve_warehouse_inventory_physical_storage_product_details,
        RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ERR_MSG,
        null,
        app_consts.GET,
        `${RETRIEVE_WAREHOUSE_INVENTORY_PHYSICAL_STORAGE_PRODUCT_DETAILS_API}${product_id}`,
        null,
        RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ACCESS_DENIED_MSG
      )
    );
  };

  const fetchHistoryDetails = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_warehouse_inventory_product_history_details,
        success_retrieve_warehouse_inventory_product_history_details,
        fail_retrieve_warehouse_inventory_product_history_details,
        RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ERR_MSG,
        null,
        app_consts.GET,
        `${RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_HISTORY_DETAILS_API}${product_id}`,
        null,
        RETRIEVE_WAREHOUSE_INVENTORY_PRODUCT_DETAILS_ACCESS_DENIED_MSG
      )
    );
  };

  // Summary call when page opens
  useEffect(() => {
    fetchMainDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-tab calls (only once)
  useEffect(() => {
    if (activeCardTab === "Physical Inventory Warehouses" && !physicalFetched) {
      setPhysicalLoading(true);
      setPhysicalError("");
      fetchPhysicalStorageDetails();
      setPhysicalFetched(true);
    }

    if (activeCardTab === "Inventory History" && !historyFetched) {
      setHistoryLoading(true);
      setHistoryError("");
      fetchHistoryDetails();
      setHistoryFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCardTab]);

  // -------------------- REDUX SELECTORS --------------------

  const main_details_reducer = useSelector(
    (state) =>
      state.retrieve_warehouse_inventory_product_main_details
        .retrieve_warehouse_inventory_product_main_details
  );

  const physical_details_reducer = useSelector(
    (state) =>
      state.retrieve_warehouse_inventory_physical_storage_product_details
        .retrieve_warehouse_inventory_physical_storage_product_details
  );

  const history_details_reducer = useSelector(
    (state) =>
      state.retrieve_warehouse_inventory_product_history_details
        .retrieve_warehouse_inventory_product_history_details
  );

  // -------------------- LISTENERS --------------------

  // Summary listener
  useEffect(() => {
    if (main_details_reducer.status === app_consts.IDLE) return;

    if (main_details_reducer.status === app_consts.LOADING) {
      setSummaryError("");
      setSummaryLoading(true);
      return;
    }

    setSummaryLoading(false);

    if (main_details_reducer.status === app_consts.FAIL) {
      setSummaryError(main_details_reducer.message);
      messageToast(main_details_reducer.message, app_consts.FAIL);
      dispatch(reset_retrieve_warehouse_inventory_product_main_details());
      return;
    }

    if (main_details_reducer.status === app_consts.SUCCESS) {
      // shape: { message, data: { product: {...} } }
      const response = main_details_reducer.data || {};
      const product = response?.data?.product || response?.product || response || null;

      setSummaryData(product);
      setProductName(product?.product_name || "");

      dispatch(reset_retrieve_warehouse_inventory_product_main_details());
      return;
    }
      
    dispatch(reset_retrieve_warehouse_inventory_product_main_details());
  }, [main_details_reducer, dispatch]);

  // Physical storage listener
  useEffect(() => {
    if (physical_details_reducer.status === app_consts.IDLE) return;

    if (physical_details_reducer.status === app_consts.LOADING) {
      setPhysicalError("");
      setPhysicalLoading(true);
      return;
    }

    setPhysicalLoading(false);

    if (physical_details_reducer.status === app_consts.FAIL) {
      setPhysicalError(physical_details_reducer.message);
      messageToast(physical_details_reducer.message, app_consts.FAIL);
      dispatch(reset_retrieve_warehouse_inventory_physical_storage_product_details());
      return;
    }

    if (physical_details_reducer.status === app_consts.SUCCESS) {
      const response = physical_details_reducer.data || {};
      const dataBlock = response?.data || response;

      const branchLocations = Array.isArray(dataBlock?.branch_locations)
        ? dataBlock.branch_locations
        : Array.isArray(dataBlock)
        ? dataBlock
        : [];

      setWarehouseListData(branchLocations);

      dispatch(reset_retrieve_warehouse_inventory_physical_storage_product_details());
      return;
    }

    dispatch(reset_retrieve_warehouse_inventory_physical_storage_product_details());
  }, [physical_details_reducer, dispatch]);

  // History listener
  useEffect(() => {
    if (history_details_reducer.status === app_consts.IDLE) return;

    if (history_details_reducer.status === app_consts.LOADING) {
      setHistoryError("");
      setHistoryLoading(true);
      return;
    }

    setHistoryLoading(false);

    if (history_details_reducer.status === app_consts.FAIL) {
      setHistoryError(history_details_reducer.message);
      messageToast(history_details_reducer.message, app_consts.FAIL);
      dispatch(reset_retrieve_warehouse_inventory_product_history_details());
      return;
    }

    if (history_details_reducer.status === app_consts.SUCCESS) {
      
      const response = history_details_reducer.data || {};
      const product = response?.data?.product || response?.product || {};
      const inv = Array.isArray(product?.inventory) ? product.inventory : [];

     let balance = 0;

const rows = [...inv]

  // 1️⃣ Normalize fields
  .map((item) => ({
    ...item,
    cap: toNum(item?.capacity),
    type: (item?.adjustment_type || "").toLowerCase(),
    timestamp: item?.inventory_history?.[0]?.timestamp || item?.date || "",
    message: item?.inventory_history?.[0]?.message || "",
  }))

  // 2️⃣ Sort chronologically
  .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

  // 3️⃣ Calculate running balance
  .map((item) => {
    if (item.type === "add") {
      balance += item.cap;
    } else {
      balance -= item.cap;
    }

    return {
      ...item,
      balance: balance.toFixed(2),
    };
  })

  // 4️⃣ Reverse only for UI display
  .reverse();

      setInventoryHistoryData(rows);

      dispatch(reset_retrieve_warehouse_inventory_product_history_details());
      return;
    }

    dispatch(reset_retrieve_warehouse_inventory_product_history_details());
  }, [history_details_reducer, dispatch]);

  // -------------------- UI --------------------

  const tabs = useMemo(
    () => [
      { tabName: "Summary" },
      { tabName: "Physical Inventory Warehouses" },
      { tabName: "Inventory History" },
    ],
    []
  );

  const adjust_inventory = (warehouse_storage_id) => {
    setInventoryModalOpen(true);
    setSelectedAdjustID(warehouse_storage_id);
  };

  const pageHeadingActions = () => {};

  const showTopLoading = summaryLoading;
  const topError = summaryError;

  return (
    <>
      <Head
        title={`Warehouse Inventory Product Details - ${
          product_name ? product_name.replace(/_/g, " ").toUpperCase() : ""
        }`}
      />
      <Content>
        <Block>
          <PageHeading
            title="Warehouse Inventory Product Details"
            title_addition={product_name?.replace(/_/g, " ").toUpperCase()}
            description="Details of Product In Warehouse Inventory"
            backToText="Warehouse Inventory"
            backToLink={`/dashboard/warehouse_inventory`}
            type={page_type}
            pageHeadingActions={pageHeadingActions}
          />

          {showTopLoading ? (
            <div className="w-full p-5 d-flex justify-center items-center">
              <Spinner />
            </div>
          ) : topError ? (
            <div className="text-soft alert-icon w-full p-5 d-flex justify-center items-center fw-bold fs-18px">
              {topError}
            </div>
          ) : (
            <>
              <CardTabs
                cardTabs={tabs}
                activeCardTab={activeCardTab}
                setActiveCardTab={setActiveCardTab}
                dataFetched={true}
              />

              <div className="card-inner">
                {activeCardTab === "Summary" && (
                  <Block>
                    <BlockHead>
                      <BlockTitle tag="h5">Warehouse Inventory Product Summary</BlockTitle>
                      <p>A Brief Summary of Product in Inventory.</p>
                    </BlockHead>

                    <div className="profile-ud-list">
                      <ProfileDetailText label="Product" text={summaryData?.product_name || "---"} />
                      <ProfileDetailText label="Product Type" text={summaryData?.product_type || "---"} />

                      <ProfileDetailText
                        label="Product Status"
                        text={summaryData?.product_status || summaryData?.account_status || "---"}
                        textClassName={
                          (summaryData?.product_status_id || summaryData?.account_status_id) === app_consts.ACTIVATE_ACCOUNT_ID
                            ? "text-success"
                            : "text-warning"
                        }
                      />

                      <ProfileDetailText
                        label="Total Inventory Capacity"
                        text={
                          summaryData?.total_warehouse_store !== undefined && summaryData?.total_warehouse_store !== null
                            ? `${toNum(summaryData?.total_warehouse_store).toFixed(2)} MT`
                            : "---"
                        }
                      />

                      <ProfileDetailText
                        label="Total Physical Capacity"
                        text={
                          summaryData?.physical_store_capacity !== undefined && summaryData?.physical_store_capacity !== null
                            ? `${toNum(summaryData?.physical_store_capacity).toFixed(2)} MT`
                            : "---"
                        }
                      />

                      <ProfileDetailText
                        label="Total Virtual Capacity"
                        text={
                          summaryData?.virtual_store_capacity !== undefined && summaryData?.virtual_store_capacity !== null
                            ? `${toNum(summaryData?.virtual_store_capacity).toFixed(2)} MT`
                            : "---"
                        }
                      />
                    </div>
                  </Block>
                )}

                {activeCardTab === "Physical Inventory Warehouses" && (
                  <Block>
                    <BlockHead>
                      <BlockTitle tag="h5">Physical Inventory Warehouses</BlockTitle>
                      <p>A List of Warehouses Where the Product Can Be Found</p>
                    </BlockHead>

                    {physicalLoading ? (
                      <div className="w-full p-5 d-flex justify-center items-center">
                        <Spinner />
                      </div>
                    ) : physicalError ? (
                      <div className="text-soft alert-icon w-full p-5 d-flex justify-center items-center fw-bold fs-18px">
                        {physicalError}
                      </div>
                    ) : (
                      <div className="my-3">
                        <ReadOnlyTable
                          data={warehouse_list_data}
                          tableType={warehouse_list_type}
                          action_function={adjust_inventory}
                        />
                      </div>
                    )}

                    <WarehouseInventoryModel
                      openModal={isInventoryModuleOpen}
                      setOpenModal={setInventoryModalOpen}
                      type={app_consts.ADJUST_INVENTORY_MODAL}
                      data={{ warehouse_storage_id: selectedAdjustID, product_id: product_id }}
                    />
                  </Block>
                )}

                {activeCardTab === "Inventory History" && (
                  <Block>
                    <BlockHead>
                      <BlockTitle tag="h5">Inventory History</BlockTitle>
                    </BlockHead>

                    {historyLoading ? (
                      <div className="w-full p-5 d-flex justify-center items-center">
                        <Spinner />
                      </div>
                    ) : historyError ? (
                      <div className="text-soft alert-icon w-full p-5 d-flex justify-center items-center fw-bold fs-18px">
                        {historyError}
                      </div>
                    ) : (
                      <div className="my-3">
                        <ReadOnlyTable data={inventory_history_data} tableType={inventory_history_list_type} />
                      </div>
                    )}
                  </Block>
                )}
              </div>
            </>
          )}
        </Block>
      </Content>
    </>
  );
};

export default WarehouseInventoryProduct;

